import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { AddressInput, type AddressData } from '@/components/ui/address-input';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { updateCustomer, deleteCustomer, getAddress } from '@vn-propane/shared/client';
import { useCustomer } from './Detail';

export default function CustomerSettings() {
  const { id: orgId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation(['admin', 'common']);
  const { customer, organization, refreshCustomer } = useCustomer();

  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [saving, setSaving] = useState(false);

  const [fullName, setFullName] = useState(customer?.fullName || '');
  const [phone, setPhone] = useState(customer?.phone || '');
  const [email, setEmail] = useState(customer?.email || '');
  const [addressData, setAddressData] = useState<AddressData | null>(null);
  const [loadingAddress, setLoadingAddress] = useState(false);

  // Sync form with customer
  useEffect(() => {
    if (customer) {
      setFullName(customer.fullName);
      setPhone(customer.phone || '');
      setEmail(customer.email || '');
    }
  }, [customer]);

  // Fetch address if customer has addressId
  useEffect(() => {
    async function fetchAddress() {
      if (!orgId || !customer?.addressId) {
        setAddressData(null);
        return;
      }

      setLoadingAddress(true);
      try {
        const result = await getAddress({
          path: { org_id: orgId, id: customer.addressId },
        });
        if (result.data?.data) {
          const address = result.data.data;
          setAddressData({
            formattedAddress: address.formattedAddress,
            address,
          });
        }
      } catch (err) {
        console.error('Failed to fetch address:', err);
      } finally {
        setLoadingAddress(false);
      }
    }

    fetchAddress();
  }, [orgId, customer?.addressId]);

  const handleUpdate = async () => {
    if (!orgId || !customer || !fullName) return;

    setSaving(true);
    const result = await updateCustomer({
      path: { org_id: orgId, id: customer.id },
      body: {
        fullName,
        phone: phone || null,
        email: email || null,
        addressId: addressData?.address?.id || null,
      },
    });

    if (result.error) {
      console.error('Failed to update customer:', result.error);
      setSaving(false);
      return;
    }

    setSaving(false);
    refreshCustomer();
  };

  const handleDelete = async () => {
    if (!orgId || !customer) return;

    const result = await deleteCustomer({
      path: { org_id: orgId, id: customer.id },
    });

    if (result.error) {
      console.error('Failed to delete customer:', result.error);
      return;
    }

    setIsDeleteOpen(false);
    setDeleteConfirmText('');
    navigate(`/organizations/${orgId}/customers`);
    refreshCustomer();
  };

  if (!customer) {
    return null;
  }

  return (
    <>
      <div className="max-w-2xl">
        <div className="space-y-6">
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">{t('customers.customerInformation')}</h3>

            <div className="space-y-2">
              <Label htmlFor="settings-name">{t('customers.fullNameRequired')}</Label>
              <Input
                id="settings-name"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="settings-phone">{t('customers.phone')}</Label>
              <Input
                id="settings-phone"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="+84 123 456 789"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="settings-email">{t('customers.email')}</Label>
              <Input
                id="settings-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="customer@example.com"
              />
              <p className="text-sm text-muted-foreground">
                {t('customers.emailHelp')}
              </p>
            </div>
          </div>

          <div className="space-y-4">
            <h3 className="text-lg font-semibold">{t('customers.address')}</h3>
            {loadingAddress ? (
              <div className="p-3 border rounded-md bg-muted/50 text-sm text-muted-foreground">
                {t('common:loading')}
              </div>
            ) : (
              <AddressInput
                organizationId={orgId || ''}
                value={addressData}
                onChange={setAddressData}
                defaultCountry={organization.default_country}
              />
            )}

            <Button onClick={handleUpdate} disabled={!fullName || saving}>
              {saving ? t('common:buttons.saving') : t('customers.saveChanges')}
            </Button>
          </div>

          <hr className="my-8" />

          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-destructive">{t('customers.dangerZone')}</h3>
            <p className="text-sm text-muted-foreground">
              {t('customers.deleteWarning')}
            </p>
            <Button variant="destructive" onClick={() => setIsDeleteOpen(true)}>
              {t('customers.deleteCustomer')}
            </Button>
          </div>
        </div>
      </div>

      <AlertDialog open={isDeleteOpen} onOpenChange={(open) => {
        setIsDeleteOpen(open);
        if (!open) setDeleteConfirmText('');
      }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('customers.deleteConfirmTitle')}</AlertDialogTitle>
            <AlertDialogDescription className="space-y-4">
              <p>
                <Trans
                  i18nKey="admin:customers.deleteConfirmMessage"
                  values={{ name: customer.fullName }}
                  components={{ strong: <strong /> }}
                />
              </p>
              <p>
                <Trans
                  i18nKey="admin:customers.deleteConfirmPrompt"
                  values={{ name: customer.fullName }}
                  components={{ strong: <strong /> }}
                />
              </p>
              <Input
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value)}
                placeholder={t('customers.typeToConfirm')}
              />
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common:buttons.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={deleteConfirmText !== customer.fullName}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {t('common:buttons.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
